import type { LogEntry } from '@/types/log-entry';
import type { Waypoint } from '@/types/waypoint';
import type {
  TimePeriod,
  SimilarDayMatch,
  BestConditionsSummary,
  LocationComparison,
  PatternAnalysisResult,
} from '@/types/patterns';

// ---------------------------------------------------------------------------
// Time period helpers
// ---------------------------------------------------------------------------

/**
 * Classify a date/time into a hunting time period.
 * Pre-dawn hunters (before 4am) fall into 'morning' by default.
 * Outside all windows (after 8pm) → 'evening'.
 */
export function getTimePeriod(date: Date | string): TimePeriod {
  const d = typeof date === 'string' ? new Date(date) : date;
  const hour = d.getHours();
  // morning: 4am–10:59am
  if (hour >= 4 && hour < 11) return 'morning';
  // midday: 11am–1:59pm
  if (hour >= 11 && hour < 14) return 'midday';
  // evening: 2pm–8pm (and after 8pm — late sitters)
  // Also: pre-dawn (0–3am) treated as morning
  if (hour >= 14) return 'evening';
  // 0–3am: pre-dawn → morning
  return 'morning';
}

// ---------------------------------------------------------------------------
// Similarity scoring
// ---------------------------------------------------------------------------

/**
 * Score how similar a historical log entry's conditions are to current conditions.
 * Returns 0–1. Entries without weather data always return 0.
 *
 * Weights:
 *   Temperature (within ±8°F): 0.40 (linear falloff to 0 at ±20°F)
 *   Pressure trend (same direction): 0.30
 *   Time of day (same period): 0.30
 */
export function computeSimilarity(
  entry: LogEntry,
  currentTemp: number,
  currentPressureTrend: 'rising' | 'falling' | 'steady',
  currentTimePeriod: TimePeriod,
): number {
  if (entry.weatherTemp === undefined || entry.weatherTemp === null) return 0;

  let score = 0;

  // Temperature: 0.40 contribution, linear falloff from 0°F diff (full) to 20°F diff (zero)
  const tempDiff = Math.abs(entry.weatherTemp - currentTemp);
  if (tempDiff <= 8) {
    // Full weight within ±8°F, linear decay from 8 to 20
    score += 0.40;
  } else if (tempDiff < 20) {
    // Linear falloff: at diff=8 → 0.40, at diff=20 → 0
    score += 0.40 * (1 - (tempDiff - 8) / 12);
  }
  // else tempDiff >= 20 → 0 contribution

  // Pressure trend: 0.30 if same direction
  const entryTrend = entry.barometricTrend ?? 'steady';
  if (entryTrend === currentPressureTrend) {
    score += 0.30;
  }

  // Time period: 0.30 if same period
  if (entry.time || entry.date) {
    const entryDateTime = entry.time
      ? new Date(`${entry.date}T${entry.time}`)
      : new Date(entry.date);
    if (!isNaN(entryDateTime.getTime())) {
      const entryPeriod = getTimePeriod(entryDateTime);
      if (entryPeriod === currentTimePeriod) {
        score += 0.30;
      }
    }
  }

  return Math.min(1, score);
}

// ---------------------------------------------------------------------------
// Sub-analyzers
// ---------------------------------------------------------------------------

export interface CurrentConditions {
  temp: number;
  pressureTrend: 'rising' | 'falling' | 'steady';
  now: Date;
}

/**
 * Find log entries at a specific location that match current conditions,
 * and report how often the user observed deer on those days.
 * Returns null if fewer than minEntries entries with weather data exist at this location.
 */
export function findSimilarDayMatches(
  entries: LogEntry[],
  waypointId: string,
  waypointName: string,
  currentConditions: CurrentConditions,
  minEntries = 3,
): SimilarDayMatch | null {
  const locationEntries = entries.filter(
    (e) => e.waypointId === waypointId && e.weatherTemp !== undefined && e.weatherTemp !== null,
  );

  if (locationEntries.length < minEntries) return null;

  const { temp, pressureTrend, now } = currentConditions;
  const currentTimePeriod = getTimePeriod(now);
  const SIMILARITY_THRESHOLD = 0.6;

  const similarEntries = locationEntries.filter(
    (e) => computeSimilarity(e, temp, pressureTrend, currentTimePeriod) >= SIMILARITY_THRESHOLD,
  );

  if (similarEntries.length === 0) return null;

  const entriesWithObservation = similarEntries.filter((e) => e.animalObserved === true).length;
  const observationRate = entriesWithObservation / similarEntries.length;

  // Build human-readable temp range: current temp ± 8°F
  const low = Math.round(temp - 8);
  const high = Math.round(temp + 8);
  const tempRange = `${low}–${high}°F`;

  return {
    type: 'similar_day',
    waypointId,
    waypointName,
    totalSimilarEntries: similarEntries.length,
    entriesWithObservation,
    observationRate,
    matchedConditions: {
      tempRange,
      pressureTrend,
      timePeriod: currentTimePeriod,
    },
    sampleSize: locationEntries.length,
    entries: similarEntries.map((e) => e.id),
  };
}

/**
 * Mode helper — returns the most common value in an array, or the first value on tie.
 */
function mode<T>(values: T[]): T {
  const counts = new Map<T, number>();
  for (const v of values) {
    counts.set(v, (counts.get(v) ?? 0) + 1);
  }
  let best: T = values[0];
  let bestCount = 0;
  for (const [val, count] of counts) {
    if (count > bestCount) {
      bestCount = count;
      best = val;
    }
  }
  return best;
}

/**
 * Compute best observation conditions across all observation entries for a property.
 * Returns null if fewer than minEntries entries with animalObserved === true and weather data exist.
 */
export function computeBestConditions(
  entries: LogEntry[],
  propertyId: string,
  propertyName: string,
  minEntries = 5,
): BestConditionsSummary | null {
  const observationEntries = entries.filter(
    (e) =>
      e.animalObserved === true &&
      e.weatherTemp !== undefined &&
      e.weatherTemp !== null,
  );

  if (observationEntries.length < minEntries) return null;

  const avgTemperature =
    observationEntries.reduce((sum, e) => sum + (e.weatherTemp ?? 0), 0) /
    observationEntries.length;

  const pressureTrends = observationEntries
    .map((e): 'rising' | 'falling' | 'steady' => e.barometricTrend ?? 'steady');
  const dominantPressureTrend = mode(pressureTrends);

  const windDirections = observationEntries
    .map((e) => e.windDirection)
    .filter((d): d is string => d !== undefined && d !== null);
  const dominantWindDirection = windDirections.length > 0 ? mode(windDirections) : 'variable';

  const windSpeeds = observationEntries
    .map((e) => e.windSpeed)
    .filter((s): s is number => s !== undefined && s !== null);
  const avgWindSpeed =
    windSpeeds.length > 0
      ? windSpeeds.reduce((sum, s) => sum + s, 0) / windSpeeds.length
      : 0;

  const timePeriods: TimePeriod[] = observationEntries.map((e) => {
    const dt = e.time ? new Date(`${e.date}T${e.time}`) : new Date(e.date);
    return isNaN(dt.getTime()) ? 'morning' : getTimePeriod(dt);
  });
  const bestTimePeriod = mode(timePeriods);

  return {
    type: 'best_conditions',
    propertyId,
    propertyName,
    avgTemperature: Math.round(avgTemperature),
    dominantPressureTrend,
    dominantWindDirection,
    avgWindSpeed: Math.round(avgWindSpeed),
    totalObservationEntries: observationEntries.length,
    bestTimePeriod,
  };
}

/**
 * Compare observation rates across locations on a property.
 * Returns null if fewer than minLocations locations have >= minEntriesPerLocation entries each.
 */
export function compareLocations(
  entries: LogEntry[],
  locations: Waypoint[],
  propertyId: string,
  minLocations = 2,
  minEntriesPerLocation = 3,
): LocationComparison | null {
  // Group entries by locationId
  const byLocation = new Map<string, LogEntry[]>();
  for (const entry of entries) {
    const list = byLocation.get(entry.waypointId) ?? [];
    list.push(entry);
    byLocation.set(entry.waypointId, list);
  }

  const qualifying = locations
    .filter((loc) => loc.propertyId === propertyId)
    .map((loc) => {
      const locEntries = byLocation.get(loc.id) ?? [];
      return { loc, locEntries };
    })
    .filter(({ locEntries }) => locEntries.length >= minEntriesPerLocation);

  if (qualifying.length < minLocations) return null;

  const locationStats = qualifying
    .map(({ loc, locEntries }) => {
      const observationVisits = locEntries.filter((e) => e.animalObserved === true).length;
      const totalVisits = locEntries.length;
      return {
        waypointId: loc.id,
        waypointName: loc.name,
        totalVisits,
        observationVisits,
        observationRate: observationVisits / totalVisits,
      };
    })
    .sort((a, b) => b.observationRate - a.observationRate);

  return {
    type: 'location_comparison',
    propertyId,
    locations: locationStats,
  };
}

// ---------------------------------------------------------------------------
// Orchestrator
// ---------------------------------------------------------------------------

/**
 * Main entry point. Runs all three sub-analyzers and returns a combined result.
 * currentConditions may be null when weather data is unavailable — in that case
 * SimilarDayMatch is skipped, but BestConditions and LocationComparison still run.
 */
export function analyzePropertyPatterns(
  propertyId: string,
  propertyName: string,
  entries: LogEntry[],
  locations: Waypoint[],
  currentConditions: CurrentConditions | null,
): PatternAnalysisResult {
  const entriesWithWeather = entries.filter(
    (e) => e.weatherTemp !== undefined && e.weatherTemp !== null,
  );
  const totalEntries = entriesWithWeather.length;

  let dataQuality: 'insufficient' | 'minimal' | 'good';
  if (totalEntries < 3) {
    dataQuality = 'insufficient';
  } else if (totalEntries < 10) {
    dataQuality = 'minimal';
  } else {
    dataQuality = 'good';
  }

  const insights: PatternAnalysisResult['insights'] = [];

  // Similar Day Match — requires current conditions and data per location
  if (currentConditions) {
    // Find the location with the most entries — most likely to meet the minimum
    const locationEntryCount = new Map<string, number>();
    for (const entry of entries) {
      if (entry.weatherTemp !== undefined && entry.weatherTemp !== null) {
        locationEntryCount.set(entry.waypointId, (locationEntryCount.get(entry.waypointId) ?? 0) + 1);
      }
    }

    // Build SimilarDayMatch for each qualifying location
    const locationMap = new Map(locations.map((l) => [l.id, l]));
    for (const [locId, count] of locationEntryCount) {
      if (count < 3) continue;
      const loc = locationMap.get(locId);
      if (!loc) continue;
      const match = findSimilarDayMatches(entries, locId, loc.name, currentConditions);
      if (match) {
        insights.push(match);
      }
    }
    // Sort similar day matches by total similar entries descending
    insights.sort((a, b) => {
      if (a.type === 'similar_day' && b.type === 'similar_day') {
        return b.totalSimilarEntries - a.totalSimilarEntries;
      }
      return 0;
    });
  }

  // Best Conditions Summary
  const bestConditions = computeBestConditions(entries, propertyId, propertyName);
  if (bestConditions) insights.push(bestConditions);

  // Location Comparison
  const locationComparison = compareLocations(entries, locations, propertyId);
  if (locationComparison) insights.push(locationComparison);

  return {
    propertyId,
    insights,
    dataQuality,
    totalEntries,
    generatedAt: new Date(),
  };
}
